import React, { useEffect, useMemo, useState } from "react";
import {
    Download, Copy, CheckCircle2, Terminal, FileCode2,
    Apple, MonitorCog, Server, Loader2, Shield, ClipboardList,
    Package, AlertTriangle, RefreshCw, ShieldAlert,
} from "lucide-react";
import { http, apiBaseUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * DeployAgentPanel — one-click onboarding for MoovBytes' C# agent.
 *
 * Admin pulls secrets once (tenant slug + install token) from the backend
 * bundle endpoint, and this component stitches them together with the current
 * `REACT_APP_BACKEND_URL` into a ready-to-save `appsettings.json`.
 */
export default function DeployAgentPanel({ chrome = true }) {
    const [bundle, setBundle] = useState(null);
    const [assets, setAssets] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("windows");
    const [copied, setCopied] = useState("");

    const apiBase = apiBaseUrl();

    useEffect(() => {
        let alive = true;
        (async () => {
            // Probe the two endpoints independently so a single-endpoint failure
            // doesn't mask the other. Bundle fails (503 "Self-registration
            // disabled") when MOOVBYTE_INSTALL_TOKEN isn't set on the server —
            // we want the user to see that specific message, not a generic
            // "Something went wrong".
            let bundleRes = null, assetsRes = null, failure = "";
            try {
                bundleRes = (await http.get("/agents/installer/bundle")).data;
            } catch (e) {
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail;
                const msg = typeof detail === "string" ? detail : (e?.message || "unknown");
                failure = `Bootstrap credentials unavailable${status ? ` (HTTP ${status})` : ""}: ${msg}`;
            }
            try {
                assetsRes = (await http.get("/v1/agents/assets?channel=stable")).data;
            } catch (e) {
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail;
                const msg = typeof detail === "string" ? detail : (e?.message || "unknown");
                // Only surface the assets error if bundle fetched OK — otherwise
                // the bundle failure is the more actionable one.
                if (!failure) {
                    failure = `Installer catalog unavailable${status ? ` (HTTP ${status})` : ""}: ${msg}`;
                }
            }
            if (!alive) return;
            if (bundleRes) setBundle(bundleRes);
            if (assetsRes) setAssets(assetsRes);
            if (failure && !bundleRes) setErr(failure);
            setLoading(false);
        })();
        return () => { alive = false; };
    }, []);

    const appsettings = useMemo(() => {
        if (!bundle) return "";
        const cfg = {
            Agent: {
                Name: `${bundle.tenant_slug}-agent`,
                Kind: "vm",
                Role: "source",
                ListenPort: bundle.default_listen_port || 9443,
                ApiBaseUrl: apiBase,
                TenantSlug: bundle.tenant_slug,
                InstallToken: bundle.install_token,
                HeartbeatIntervalSeconds: 15,
                JobPollIntervalSeconds: 5,
                CommandPollIntervalSeconds: 5,
                ParallelTransfers: 4,
                BlockSizeBytes: bundle.block_size_bytes || 4 * 1024 * 1024,
                ContinuousSyncEnabled: false,
                ContinuousSyncPaths: [],
                OfflineQueuePath: "./data/offline-queue.json",
                StateDirectory: "./data/state",
                TokenStorePath: "./data/token.bin",
                AutoUpdateEnabled: true,
                UpdateChannel: "stable",
                UpdateIntervalHours: 6,
                CurrentVersion: bundle.agent_version || "1.0.0",
                UpdateBaseDir: "./agent",
                PeerIngestEnabled: true,
                DestinationRoot: "./destination",
                RequireTLS: false,
                EnableVersioning: true,
                // Quantum-Safe Transfer downgrade policy: "Preferred" (default —
                // attempt hybrid X25519+ML-KEM-768, fall back to classical if the
                // peer doesn't support it), "Required" (fail closed instead), or
                // "Disabled". Changeable later without reinstalling from the
                // agent's card on the Devices page.
                SecurityPolicy: "Preferred",
            },
            Logging: { LogLevel: { Default: "Information" } },
        };
        return JSON.stringify(cfg, null, 2);
    }, [bundle, apiBase]);

    const installCommands = {
        windows:
            "# 1. Extract MoovByte.Agent.zip to C:\\Program Files\\MoovBytes\n" +
            "# 2. Save appsettings.json into that folder (overwrite the bundled one)\n" +
            "# 3. Install as a Windows Service (run PowerShell as Admin):\n" +
            "sc.exe create MoovByteAgent binPath=\"C:\\Program Files\\MoovBytes\\MoovByte.Agent.exe\" start=auto\n" +
            "sc.exe start MoovByteAgent",
        linux:
            "# Three install paths — pick the one that matches your distro.\n" +
            "# ┄ Debian / Ubuntu (.deb) ─ recommended for fleet rollouts via apt:\n" +
            "sudo apt install ./moovbytes-agent_amd64.deb\n" +
            "sudo moovbytes-agent-setup              # interactive enrolment wizard\n" +
            "\n" +
            "# ┄ RHEL / Fedora / Rocky / Alma (.rpm):\n" +
            "sudo dnf install ./moovbytes-agent.rpm\n" +
            "sudo moovbytes-agent-setup\n" +
            "\n" +
            "# ┄ Portable tar.gz (any glibc 2.31+ host — no package manager):\n" +
            "tar -xzf moovbytes-agent-linux-x64.tar.gz\n" +
            "sudo bash install.sh\n" +
            "sudo nano /etc/moovbytes/config.json   # paste tenant + agent token\n" +
            "sudo systemctl restart moovbytes-agent\n" +
            "\n" +
            "# Tail the live logs (Ctrl-C exits):\n" +
            "sudo journalctl -u moovbytes-agent -f",
        macos:
            "# 1. Extract — the tarball's install.sh installs the binary to\n" +
            "#    /usr/local/moovbytes and registers a launchd daemon.\n" +
            "tar -xzf moovbytes-agent-macos-arm64.tar.gz\n" +
            "sudo bash install.sh\n" +
            "# 2. Paste your tenant + agent token from this dialog:\n" +
            "sudo nano \"/Library/Application Support/MoovBytes/config.json\"\n" +
            "sudo launchctl bootstrap system /Library/LaunchDaemons/io.moovbytes.agent.plist\n" +
            "# 3. Tail logs:\n" +
            "sudo tail -f /Library/Logs/MoovBytes/agent.err.log\n" +
            "# Note: First run triggers the macOS Gatekeeper prompt — right-click\n" +
            "# /usr/local/moovbytes/MoovByte.Agent → Open once to authorise.",
    };

    const copyText = async (value, key) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(key);
            setTimeout(() => setCopied(""), 1600);
        } catch {
            // Fallback: nothing to do — user can still select manually.
        }
    };

    const download = (filename, content, mime = "application/json") => {
        const blob = new Blob([content], { type: mime });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
    };

    const tabs = [
        { id: "windows", label: "Windows", icon: MonitorCog },
        { id: "linux", label: "Linux", icon: Server },
        { id: "macos", label: "macOS", icon: Apple },
    ];

    const currentAsset = assets?.assets?.[tab];
    const isWizard = currentAsset?.setup_type === "wizard";

    return (
        <>
            {chrome && (
                <div className="mb-5 flex items-start gap-3" data-testid="deploy-agent-page-header">
                    <DeployAgentHeader bundle={bundle} />
                </div>
            )}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                    <Loader2 size={16} className="animate-spin" /> Loading bundle…
                </div>
            ) : err ? (
                <div className="px-6 py-12 text-center text-sm text-[hsl(var(--destructive))]" data-testid="deploy-agent-error">
                    {err}
                </div>
            ) : (
                <div className={chrome ? "space-y-5" : "px-6 py-5 space-y-5"}>
                    {/* Credentials strip */}
                    <div className="rounded-xl border border-border bg-background p-4" data-testid="deploy-agent-creds-strip">
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                <Shield size={12} /> Bootstrap Credentials
                            </div>
                            <RotateInstallTokenButton
                                onRotated={(newToken) => setBundle((b) => b ? { ...b, install_token: newToken } : b)}
                            />
                        </div>
                        <CredRow label="Tenant Slug" value={bundle.tenant_slug}
                            onCopy={() => copyText(bundle.tenant_slug, "slug")} copied={copied === "slug"}
                            testId="deploy-agent-tenant-slug" />
                        <CredRow label="API Base URL" value={apiBase}
                            onCopy={() => copyText(apiBase, "url")} copied={copied === "url"}
                            testId="deploy-agent-api-url" />
                        <CredRow label="Install Token" value={bundle.install_token} mono
                            onCopy={() => copyText(bundle.install_token, "token")} copied={copied === "token"}
                            testId="deploy-agent-install-token" />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                            The install token is a <b>per-tenant</b> bootstrap secret. After an agent
                            registers, it&apos;s replaced by a per-agent token stored on the host.
                            Rotate any time — already-registered agents keep working.
                        </p>
                    </div>

                    {/* Windows SmartScreen guidance — only visible on the Windows tab. */}
                    {tab === "windows" && <SmartScreenNotice />}

                    {/* Primary action — download appsettings (portable installs only) */}
                    {!isWizard && (
                        <div className="flex flex-col gap-3 rounded-xl bg-gradient-to-r from-[#5b5ef0] to-[#7c3aed] p-5 text-white shadow-lg sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <div className="flex items-center gap-2 font-display text-base font-bold">
                                <FileCode2 size={18} /> appsettings.json
                            </div>
                            <p className="mt-1 text-xs text-white/80">
                                Drop this next to <code className="rounded bg-white/10 px-1 py-0.5">MoovByte.Agent.exe</code> — secrets already baked in.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => copyText(appsettings, "appsettings")}
                                className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-2 text-sm font-semibold backdrop-blur transition hover:bg-white/25"
                                data-testid="deploy-agent-copy-appsettings"
                            >
                                {copied === "appsettings" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                                {copied === "appsettings" ? "Copied" : "Copy"}
                            </button>
                            <button
                                onClick={() => download("appsettings.json", appsettings)}
                                className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[#5b5ef0] shadow hover:bg-white/95"
                                data-testid="deploy-agent-download-appsettings"
                            >
                                <Download size={14} /> Download
                            </button>
                        </div>
                    </div>
                    )}

                    {/* Config preview (portable installs only) */}
                    {!isWizard && (
                        <details className="rounded-lg border border-border bg-background">
                        <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted">
                            Preview appsettings.json
                        </summary>
                        <pre
                            className="max-h-64 overflow-auto border-t border-border bg-[#0b0f1a] p-4 text-[11px] leading-relaxed text-[#d1d5db]"
                            data-testid="deploy-agent-appsettings-preview"
                        >{appsettings}</pre>
                    </details>
                    )}

                    {/* Installer downloads — per-OS binary (portable tabs only; wizard
                        tabs render their own big download hero inside WizardInstructions) */}
                    {!isWizard && (
                        <InstallerSection
                            assets={assets}
                            apiBaseUrl={apiBase}
                            currentTab={tab}
                        />
                    )}

                    {/* OS picker tabs — always visible */}
                    <div>
                        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {isWizard ? <><Download size={12} /> Choose your OS</> : <><Terminal size={12} /> Install Commands</>}
                        </div>
                        <div className="flex gap-1 border-b border-border">
                            {tabs.map((t) => {
                                const Icon = t.icon;
                                const active = tab === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setTab(t.id)}
                                        className={[
                                            "inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold transition",
                                            active
                                                ? "border-[#5b5ef0] text-[#5b5ef0]"
                                                : "border-transparent text-muted-foreground hover:text-foreground",
                                        ].join(" ")}
                                        data-testid={`deploy-agent-tab-${t.id}`}
                                    >
                                        <Icon size={14} /> {t.label}
                                    </button>
                                );
                            })}
                        </div>
                        {isWizard ? (
                            <WizardInstructions os={tab} apiBaseUrl={apiBase} tenantSlug={bundle.tenant_slug} installToken={bundle.install_token} />
                        ) : (
                            <div className="relative">
                                <pre
                                    className="max-h-60 overflow-auto rounded-b-lg bg-[#0b0f1a] p-4 text-[11px] leading-relaxed text-[#d1d5db]"
                                    data-testid={`deploy-agent-commands-${tab}`}
                                >{installCommands[tab]}</pre>
                                <button
                                    onClick={() => copyText(installCommands[tab], `cmd-${tab}`)}
                                    className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-[11px] font-semibold text-white hover:bg-white/20"
                                    data-testid={`deploy-agent-copy-commands-${tab}`}
                                >
                                    {copied === `cmd-${tab}` ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                                    {copied === `cmd-${tab}` ? "Copied" : "Copy"}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Next-steps */}
                    <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
                        <div className="mb-1 flex items-center gap-2 font-semibold text-foreground">
                            <ClipboardList size={14} /> After first start
                        </div>
                        <ol className="list-inside list-decimal space-y-1 text-muted-foreground">
                            <li>Agent self-registers and appears on <span className="font-semibold text-foreground">/agents</span> within ~15 s.</li>
                            <li>Live hardware (CPU / RAM / disks / network) replaces the simulated profile.</li>
                            <li>Download a printable inventory PDF any time from the agent's detail panel.</li>
                        </ol>
                    </div>
                </div>
            )}
        </>
    );
}

function WizardInstructions({ os = "windows", apiBaseUrl, tenantSlug, installToken }) {
    const downloadUrl = `${apiBaseUrl}/api/v1/agents/download?os=${os}&channel=stable`;

    // Per-OS wizard copy. The structure (hero + 3-step ol + security note)
    // stays identical so the modal feels symmetric across platforms; only
    // the artifact name, the UAC/Gatekeeper terminology, and the secret-
    // store backend (DPAPI vs Keychain) change.
    const variants = {
        windows: {
            asset: "MoovByteAgentSetup.exe",
            tagline: "One-click installer · signs up the agent, installs the Windows service, and starts it. No config files to edit.",
            heroGradient: "from-[#0b1f4a] via-[#1e3a8a] to-[#5b5ef0]",
            heroText: "text-[#0b1f4a]",
            steps: [
                {
                    label: "Run the installer",
                    body: <>Double-click <code className="rounded bg-muted px-1 py-0.5 text-[11px]">MoovByteAgentSetup.exe</code> and approve the UAC prompt.</>,
                },
                {
                    label: "Paste credentials when prompted",
                    body: <>
                        Paste <span className="font-semibold">Tenant slug</span> <code className="rounded bg-muted px-1 py-0.5 text-[11px]">{tenantSlug}</code> and
                        your install token — the API URL isn't shown, it's already built in. Click <span className="font-semibold">Test Connection</span> to verify.
                        Skipped automatically if you used the unattended command above.
                    </>,
                },
                {
                    label: "Click Install",
                    body: "The wizard creates a Windows service, starts it, and the agent registers automatically. Done.",
                },
            ],
            security: <>
                Token is DPAPI-encrypted (LocalMachine scope) and written only to
                <code className="mx-1 rounded bg-[#10b981]/10 px-1 py-0.5 text-[11px]">C:\ProgramData\MoovBytes\config.json</code>
                with SYSTEM + Administrators-only ACLs. No plaintext on disk.
            </>,
        },
        macos: {
            asset: "MoovByteAgent.pkg",
            tagline: "Signed + notarised .pkg · drops the agent under /usr/local/moovbytes/agent and registers a LaunchDaemon. Universal binary (Apple Silicon + Intel).",
            heroGradient: "from-[#0f172a] via-[#1e293b] to-[#475569]",
            heroText: "text-[#0f172a]",
            steps: [
                {
                    label: "Open the .pkg",
                    body: <>
                        Double-click <code className="rounded bg-muted px-1 py-0.5 text-[11px]">MoovByteAgent.pkg</code>.
                        Apple notarisation means Gatekeeper accepts it without right-click → Open.
                    </>,
                },
                {
                    label: "Walk through the Installer wizard",
                    body: "Welcome → License → Install. The package authenticates with your admin password and lays down the binary, LaunchDaemon, and a hidden _moovbytes service user.",
                },
                {
                    label: "Run the first-time setup in Terminal",
                    body: <>
                        Open Terminal and run <code className="rounded bg-muted px-1 py-0.5 text-[11px]">sudo moovbytes-agent-setup</code>.
                        Paste your tenant slug <code className="rounded bg-muted px-1 py-0.5 text-[11px]">{tenantSlug}</code> and
                        install token when prompted — the daemon loads automatically.
                    </>,
                },
            ],
            security: <>
                Install token is stored in the <span className="font-semibold">macOS System Keychain</span>
                (<code className="mx-1 rounded bg-[#10b981]/10 px-1 py-0.5 text-[11px]">security add-generic-password … -T MoovByte.Agent</code>),
                readable only by root and the signed agent binary. The native analogue of Windows DPAPI LocalMachine scope.
            </>,
        },
    };

    const v = variants[os] || variants.windows;

    return (
        <div className="rounded-b-lg border border-t-0 border-border bg-background p-5" data-testid="deploy-agent-wizard-pane">
            <div className={`flex flex-col gap-3 rounded-xl bg-gradient-to-r ${v.heroGradient} p-5 text-white shadow-lg sm:flex-row sm:items-center sm:justify-between`}>
                <div className="flex-1">
                    <div className="flex items-center gap-2 font-display text-lg font-bold">
                        <Package size={18} /> {v.asset}
                    </div>
                    <p className="mt-1 text-xs text-white/80">{v.tagline}</p>
                </div>
                <a
                    href={downloadUrl}
                    className={`inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold ${v.heroText} shadow hover:bg-white/95`}
                    data-testid="deploy-agent-wizard-download"
                >
                    <Download size={15} /> Download installer
                </a>
            </div>

            {os === "windows" && (
                <WindowsInstallCommand tenantSlug={tenantSlug} installToken={installToken} />
            )}

            <ol className="mt-5 space-y-3 text-sm" data-testid="deploy-agent-wizard-steps">
                {v.steps.map((s, i) => (
                    <WizardStep key={s.label} n={i + 1} label={s.label}>
                        {s.body}
                    </WizardStep>
                ))}
            </ol>

            <div className="mt-5 flex items-start gap-2 rounded-md border border-[#10b981]/30 bg-[#10b981]/5 p-3 text-xs text-[#065f46] dark:text-[#6ee7b7]">
                <Shield size={14} className="mt-0.5 shrink-0" />
                <span>{v.security}</span>
            </div>
        </div>
    );
}

function WizardStep({ n, label, children }) {
    return (
        <li className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#5b5ef0] font-display text-xs font-bold text-white">
                {n}
            </span>
            <div>
                <div className="font-semibold text-foreground">{label}</div>
                <div className="text-muted-foreground">{children}</div>
            </div>
        </li>
    );
}

/**
 * WindowsInstallCommand — the fully-unattended alternative to clicking
 * through the wizard. MoovByteAgent.iss reads /TenantSlug and /InstallToken
 * as named command-line params (ExpandConstant('{param:…}')), pre-fills
 * the Connect page with them, and — since HasCliCredentials is true —
 * skips the Connect/Device/Settings pages entirely. So this one line is
 * the whole install: download, then run it, done. /ApiUrl is deliberately
 * omitted: the API base URL is no longer a user-facing concept at all
 * (GetApiBaseUrl in the .iss always resolves the real one internally).
 */
function WindowsInstallCommand({ tenantSlug, installToken }) {
    const [copied, setCopied] = useState(false);
    const command = `MoovByteAgentSetup.exe /VERYSILENT /TenantSlug=${tenantSlug} /InstallToken=${installToken}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(command);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {
            // Fallback: nothing to do — user can still select manually.
        }
    };

    return (
        <div
            className="mt-4 rounded-lg border border-[#5b5ef0]/30 bg-[#5b5ef0]/5 p-3.5"
            data-testid="deploy-agent-windows-install-command"
        >
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#5b5ef0]">
                <Terminal size={12} /> Recommended: unattended install
            </div>
            <p className="mb-2 text-[11px] text-muted-foreground">
                After downloading, run this from an elevated Command Prompt in the same
                folder. Credentials are baked in — no UAC prompts to click through, no
                fields to fill in.
            </p>
            <div className="relative">
                <pre
                    className="overflow-x-auto rounded-md bg-[#0b0f1a] p-3 pr-16 text-[11px] leading-relaxed text-[#d1d5db]"
                    data-testid="deploy-agent-windows-install-command-text"
                >{command}</pre>
                <button
                    onClick={copy}
                    className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-[11px] font-semibold text-white hover:bg-white/20"
                    data-testid="deploy-agent-copy-windows-command"
                >
                    {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
        </div>
    );
}

function DeployAgentHeader({ bundle }) {
    return (
        <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#ede9fe] text-[#5b5ef0]">
                <Download size={22} />
            </div>
            <div>
                <h2 className="font-display text-xl font-bold">Download the MoovBytes Agent</h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                    Pre-configured for <span className="font-semibold text-foreground">{bundle?.tenant_name || "…"}</span>.
                    Pick your OS below, download the installer, and paste your token when prompted — no config files to edit.
                </p>
            </div>
        </div>
    );
}

function CredRow({ label, value, mono, onCopy, copied, testId }) {
    return (
        <div className="flex items-center justify-between gap-3 border-b border-border/60 py-2 last:border-b-0">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
            <div className="flex flex-1 items-center justify-end gap-2">
                <code
                    className={[
                        "truncate text-right",
                        mono ? "font-mono text-[11px]" : "text-xs",
                    ].join(" ")}
                    data-testid={testId}
                >{value}</code>
                <button
                    onClick={onCopy}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Copy"
                    data-testid={`${testId}-copy`}
                >
                    {copied ? <CheckCircle2 size={14} className="text-[hsl(var(--success))]" /> : <Copy size={14} />}
                </button>
            </div>
        </div>
    );
}

function fmtBytes(n) {
    if (!n) return "—";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * InstallerSection — surfaces the per-OS binary download state.
 *
 * Modal is opened BEFORE any CI job has run in fresh installs, so this
 * MUST degrade gracefully: show a build-this-first hint when the MSI /
 * tar.gz / pkg isn't on disk yet, rather than a dead button.
 *
 * Linux is special: it has three discrete artifact families
 * (`linux` = portable tar.gz, `linux-deb` = Debian/Ubuntu package,
 * `linux-rpm` = RHEL/Fedora/Rocky/Alma package). We render all three
 * as side-by-side cards on the Linux tab so operators can pick the
 * one that matches their distro without leaving the page.
 */
function InstallerSection({ assets, apiBaseUrl, currentTab }) {
    if (!assets) return null;

    // On the Linux tab, surface tar.gz + deb + rpm as siblings. Each
    // package manager has a different install command, so the card
    // exposes a one-liner hint below the metadata row.
    if (currentTab === "linux") {
        const linuxVariants = [
            { key: "linux",     hint: "tar -xzf … && sudo bash install.sh", testLabel: "tarball" },
            { key: "linux-deb", hint: "sudo apt install ./moovbytes-agent_amd64.deb", testLabel: "deb" },
            { key: "linux-rpm", hint: "sudo dnf install ./moovbytes-agent.rpm",        testLabel: "rpm" },
        ];
        return (
            <div className="rounded-xl border border-border bg-background p-4" data-testid="deploy-agent-installer-section">
                <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <Package size={12} /> Linux Installer Binaries
                    {assets.version && (
                        <span className="ml-auto font-mono text-[10px] normal-case tracking-normal text-muted-foreground">
                            v{assets.version} · {assets.channel}
                        </span>
                    )}
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                    {linuxVariants.map(({ key, hint, testLabel }) => (
                        <LinuxVariantCard
                            key={key}
                            osKey={key}
                            testLabel={testLabel}
                            entry={assets.assets?.[key]}
                            apiBaseUrl={apiBaseUrl}
                            installHint={hint}
                            version={assets.version}
                        />
                    ))}
                </div>
                <p className="mt-3 text-[11px] text-muted-foreground" data-testid="deploy-agent-linux-arch-hint">
                    Binaries above are <span className="font-mono">x86_64</span>. ARM64 hosts
                    (AWS Graviton, Ampere Altra, Raspberry Pi 4/5) — use the arm64 tarball
                    download on the tar.gz card.
                </p>
            </div>
        );
    }

    // Single-artifact path (Windows .exe, macOS .pkg).
    const entry = assets.assets?.[currentTab];
    if (!entry) return null;
    const available = !!entry.available;
    const downloadUrl = entry.download_url ? `${apiBaseUrl}${entry.download_url}` : null;

    return (
        <div
            className="rounded-xl border border-border bg-background p-4"
            data-testid="deploy-agent-installer-section"
        >
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Package size={12} /> Installer Binary
                {assets.version && (
                    <span className="ml-auto font-mono text-[10px] normal-case tracking-normal text-muted-foreground">
                        v{assets.version} · {assets.channel}
                    </span>
                )}
            </div>

            {available ? (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                        <div className="font-display text-sm font-bold" data-testid="deploy-agent-installer-label">
                            {entry.label}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                            <span data-testid="deploy-agent-installer-size">{fmtBytes(entry.size_bytes)}</span>
                            {entry.checksum && (
                                <span className="font-mono">
                                    sha256: {entry.checksum.slice(0, 12)}…
                                </span>
                            )}
                        </div>
                    </div>
                    <a
                        href={downloadUrl}
                        download
                        className="inline-flex items-center gap-2 self-start rounded-lg bg-foreground px-3.5 py-2 text-sm font-semibold text-background transition hover:opacity-90 sm:self-auto"
                        data-testid={`deploy-agent-download-binary-${currentTab}`}
                    >
                        <Download size={14} /> Download Installer
                    </a>
                </div>
            ) : (
                <div
                    className="flex items-start gap-2 rounded-lg border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-3 text-xs text-foreground"
                    data-testid={`deploy-agent-installer-missing-${currentTab}`}
                >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[#f59e0b]" />
                    <div>
                        <div className="font-semibold">{entry.label} not built yet</div>
                        <div className="mt-1 text-muted-foreground">
                            {assets.version ? (
                                <>Release <span className="font-mono">v{assets.version}</span> is
                                 published but no installer binary has been uploaded for this
                                 platform. An admin can push one via{" "}
                                <code className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
                                    POST /api/v1/agents/releases/{assets.version}/installer/{currentTab}
                                </code>.</>
                            ) : (
                                <>No agent release has been published to the <b>stable</b>{" "}
                                channel yet. Upload one via{" "}
                                <code className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
                                    POST /api/v1/agents/releases
                                </code> then push the per-OS installer.</>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function LinuxVariantCard({ osKey, testLabel, entry, apiBaseUrl, installHint, version }) {
    if (!entry) return null;
    const available = !!entry.available;
    const downloadUrl = entry.download_url ? `${apiBaseUrl}${entry.download_url}` : null;
    const fallbackUrl = entry.fallback_available && entry.fallback_download_url
        ? `${apiBaseUrl}${entry.fallback_download_url}`
        : null;

    return (
        <div
            className="flex flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3"
            data-testid={`deploy-agent-linux-variant-${testLabel}`}
        >
            <div className="font-display text-sm font-bold leading-tight">{entry.label}</div>
            {available ? (
                <>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                        <span>{fmtBytes(entry.size_bytes)}</span>
                        {entry.checksum && (
                            <span className="font-mono">{entry.checksum.slice(0, 10)}…</span>
                        )}
                    </div>
                    <code className="rounded bg-background/60 px-2 py-1 font-mono text-[10.5px] leading-snug text-foreground/80">
                        {installHint}
                    </code>
                    <a
                        href={downloadUrl}
                        download
                        className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-background transition hover:opacity-90"
                        data-testid={`deploy-agent-download-${testLabel}`}
                    >
                        <Download size={12} /> Download
                    </a>
                    {fallbackUrl && (
                        <a
                            href={fallbackUrl}
                            download
                            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-border px-3 py-1 text-[11px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
                            data-testid={`deploy-agent-download-${testLabel}-fallback`}
                            title={entry.fallback_label}
                        >
                            <Download size={11} />{" "}
                            {entry.fallback_label || "Alternate"}
                            {entry.fallback_size_bytes && (
                                <span className="font-mono text-[10px] opacity-70">
                                    · {fmtBytes(entry.fallback_size_bytes)}
                                </span>
                            )}
                        </a>
                    )}
                </>
            ) : (
                <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0 text-[#f59e0b]" />
                    <span>
                        Not built yet. Push via{" "}
                        <code className="font-mono text-[10px]">
                            /releases/{version || "{version}"}/installer/{osKey}
                        </code>.
                    </span>
                </div>
            )}
        </div>
    );
}


/* ---- Install-token rotation ----------------------------------------------
 *
 * Lives next to the Bootstrap Credentials header so the rotate action is
 * spatially associated with the secret it affects. Two-step confirm to
 * prevent fat-finger rotations (already-installed agents keep working,
 * but freshly-downloaded installers stop matching the old value).
 */
function RotateInstallTokenButton({ onRotated }) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);

    const rotate = async () => {
        setBusy(true);
        try {
            const { data } = await http.post("/agents/install-token/rotate");
            onRotated?.(data.install_token);
            toast.success("Install token rotated. Re-download your installer.");
            setConfirming(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Rotation failed");
        } finally {
            setBusy(false);
        }
    };

    if (!confirming) {
        return (
            <button
                type="button"
                onClick={() => setConfirming(true)}
                data-testid="deploy-agent-rotate-token-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground transition hover:border-[#f59e0b]/50 hover:text-[#f59e0b]"
                title="Generate a new bootstrap secret"
            >
                <RefreshCw size={11} /> Rotate
            </button>
        );
    }
    return (
        <div className="flex items-center gap-1.5" data-testid="deploy-agent-rotate-token-confirm">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-[#f59e0b]">
                Sure?
            </span>
            <button
                type="button"
                onClick={rotate}
                disabled={busy}
                data-testid="deploy-agent-rotate-token-confirm-btn"
                className="inline-flex items-center gap-1 rounded-md bg-[#f59e0b] px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white transition hover:brightness-110 disabled:opacity-50"
            >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                Yes, rotate
            </button>
            <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="inline-flex items-center rounded-md border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
                Cancel
            </button>
        </div>
    );
}

/* ---- Windows SmartScreen pre-empt -----------------------------------------
 *
 * Until we ship an EV-signed installer, Windows 10/11 will throw a blue
 * "Windows protected your PC" SmartScreen prompt on first run. Pre-empting
 * the friction inline (instead of letting the user hit it cold) is the
 * difference between "this is broken" tickets and a 10-second click-through.
 */
function SmartScreenNotice() {
    return (
        <div
            className="flex gap-3 rounded-xl border border-[#0ea5e9]/30 bg-[#0ea5e9]/[0.06] p-4"
            data-testid="deploy-agent-smartscreen-notice"
        >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0ea5e9]/15 text-[#0ea5e9]">
                <ShieldAlert size={18} />
            </div>
            <div className="flex-1">
                <div className="text-sm font-semibold">
                    Windows may say <span className="font-mono">&ldquo;Unknown publisher&rdquo;</span> &mdash; that&apos;s expected.
                </div>
                <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                    SmartScreen flags every freshly-built installer until it builds reputation.
                    Click <b>More info</b> &rarr; <b>Run anyway</b> to proceed. The MSI is published
                    by MoovBytes and only writes to <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">C:\Program Files\MoovBytes&nbsp;Agent</code>{" "}
                    and the local Windows service registry.
                </p>
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                    We&apos;re working towards an EV-signed installer that bypasses this warning entirely.
                </p>
            </div>
        </div>
    );
}
